import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PublicNav, PublicFooter } from "@/components/PublicShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Compass, Crown, Layers, Building2, Check, Rocket, Landmark, Sparkles, Boxes, GraduationCap, Users } from "lucide-react";
import { toast } from "sonner";

const HERO =
  "https://images.pexels.com/photos/1313534/pexels-photo-1313534.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const OPPORTUNITIES = [
  "Accelerator leadership",
  "Executive Director roles",
  "Venture Capital positions",
  "Entrepreneur-in-Residence opportunities",
  "Corporate Innovation leadership",
  "Startup executive roles",
  "Fractional leadership",
  "Board opportunities",
];

const EMPLOYERS = [
  { icon: Rocket, label: "Venture-backed startups", note: "Seed to growth stage" },
  { icon: Landmark, label: "Venture capital funds", note: "Platform & portfolio roles" },
  { icon: Sparkles, label: "Accelerators", note: "Program & operating teams" },
  { icon: Boxes, label: "Venture studios", note: "Founders-in-residence" },
  { icon: Building2, label: "Corporate innovation", note: "New-venture & lab teams" },
  { icon: GraduationCap, label: "University & research spinouts", note: "Commercialization leaders" },
];

const BENEFITS = [
  { icon: Crown, title: "Leadership across every mandate", body: "Be considered for executive, platform, operational, venture, board, fractional and advisory roles across the innovation economy." },
  { icon: Compass, title: "Signal your interests", body: "Tell us the role types, industries and functions you want — we only surface opportunities that fit." },
  { icon: Layers, title: "One managed profile", body: "Maintain a single profile and resume. Track every application and interview status in one place." },
];

const STEPS = [
  "Create your profile & signal your interests",
  "Our recruiters match you to live searches",
  "Track interviews & opportunities in your portal",
];

export default function ForCandidates() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    full_name: "", email: "", password: "", headline: "", location: "",
    linkedin_url: "", industries: "", functions: "", availability_status: "open", resume: null,
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toList = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await register({ full_name: f.full_name, email: f.email, password: f.password, role: "candidate" });
      await api.patch("/me/candidate", {
        headline: f.headline, location: f.location, linkedin_url: f.linkedin_url,
        industries: toList(f.industries), functions: toList(f.functions),
        availability_status: f.availability_status,
        board_interest: false, fractional_interest: false, advisor_interest: false,
        geographic_preferences: [], summary: "", compensation_expectations: "", phone: "",
      });
      if (f.resume) {
        const fd = new FormData();
        fd.append("file", f.resume);
        await api.post("/me/candidate/resume", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Welcome — your candidate profile is live");
      navigate("/candidate");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#F9F9FA] text-[#09090B]">
      <PublicNav />
      <section className="relative pt-16">
        <div className="max-w-[1400px] mx-auto px-6 py-16 lg:py-24 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <p className="overline mb-4">For Candidates</p>
            <h1 className="font-heading font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              The rooms where<br /><span className="text-primary">leadership happens.</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-lg">
              Innovation Talent Partners represents exceptional operators, founders and executives across
              the innovation economy — from first commercial hires to the C-suite, spanning executive,
              platform, operational, venture, board and fractional mandates. We work with the teams
              building what's next, and match you to searches that actually fit your ambitions.
            </p>
            <p className="mt-4 text-lg text-muted-foreground max-w-lg">
              Create one managed profile, signal your interests, and let the right opportunities find you.
            </p>

            <div className="mt-10">
              <p className="overline mb-4">Where our candidates land</p>
              <div className="grid grid-cols-2 gap-px bg-black/10 border border-black/10">
                {EMPLOYERS.map((c, i) => (
                  <motion.div key={c.label} data-testid={`employer-type-${i}`}
                    initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                    className="bg-white p-4 flex items-start gap-3 hover:bg-[#F4F4F5] transition-colors">
                    <c.icon className="h-5 w-5 text-primary shrink-0 mt-0.5" strokeWidth={1.5} />
                    <div>
                      <p className="font-heading font-bold text-sm leading-tight">{c.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{c.note}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="mt-10 space-y-px bg-black/10 border border-black/10">
              {BENEFITS.map((b, i) => (
                <motion.div key={b.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                  className="bg-white p-6 flex gap-4">
                  <b.icon className="h-6 w-6 text-primary shrink-0" strokeWidth={1.5} />
                  <div>
                    <h3 className="font-heading font-bold">{b.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{b.body}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="mt-10">
              <p className="overline mb-4">How it works</p>
              <ol className="space-y-3">
                {STEPS.map((s, i) => (
                  <li key={s} className="flex gap-4 items-center">
                    <span className="font-mono text-xs text-primary">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-sm">{s}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* Signup form */}
          <div className="lg:col-span-6 lg:sticky lg:top-24">
            <div className="border border-black/10 bg-white">
              <div className="relative h-28 overflow-hidden border-b border-black/10">
                <img src={HERO} alt="" className="w-full h-full object-cover opacity-40" />
                <div className="absolute inset-0 bg-gradient-to-r from-white via-white/80 to-transparent flex items-center px-8">
                  <div>
                    <p className="overline">Join as a candidate</p>
                    <p className="font-heading font-bold text-xl">Create your profile</p>
                  </div>
                </div>
              </div>
              <form onSubmit={submit} className="p-8 space-y-4" data-testid="candidate-signup-form">
                <div className="border border-black/10 bg-[#F4F4F5] p-5" data-testid="candidate-opportunities">
                  <p className="text-sm font-medium">Create your professional profile to be considered for:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 mt-3">
                    {OPPORTUNITIES.map((o) => (
                      <div key={o} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" strokeWidth={2.5} />
                        <span>{o}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Full name</Label><Input data-testid="cand-signup-name" value={f.full_name} onChange={set("full_name")} required className="bg-[#F4F4F5] border-black/10 rounded-none" /></div>
                  <div className="space-y-2"><Label>Email</Label><Input type="email" data-testid="cand-signup-email" value={f.email} onChange={set("email")} required className="bg-[#F4F4F5] border-black/10 rounded-none" /></div>
                </div>
                <div className="space-y-2"><Label>Password</Label><Input type="password" data-testid="cand-signup-password" value={f.password} onChange={set("password")} required className="bg-[#F4F4F5] border-black/10 rounded-none" /></div>
                <div className="space-y-2"><Label>Headline</Label><Input value={f.headline} onChange={set("headline")} placeholder="Ex-VP Product, Series B SaaS" className="bg-[#F4F4F5] border-black/10 rounded-none" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Location</Label><Input value={f.location} onChange={set("location")} className="bg-[#F4F4F5] border-black/10 rounded-none" /></div>
                  <div className="space-y-2">
                    <Label>Availability</Label>
                    <Select value={f.availability_status} onValueChange={(v) => setF({ ...f, availability_status: v })}>
                      <SelectTrigger className="bg-[#F4F4F5] border-black/10 rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white border-black/10">
                        <SelectItem value="open">Open to opportunities</SelectItem>
                        <SelectItem value="passive">Passive / listening</SelectItem>
                        <SelectItem value="not_looking">Not looking</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2"><Label>LinkedIn URL</Label><Input value={f.linkedin_url} onChange={set("linkedin_url")} placeholder="https://linkedin.com/in/" className="bg-[#F4F4F5] border-black/10 rounded-none" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Industries</Label><Input value={f.industries} onChange={set("industries")} placeholder="fintech, ai" className="bg-[#F4F4F5] border-black/10 rounded-none" /></div>
                  <div className="space-y-2"><Label>Functions</Label><Input value={f.functions} onChange={set("functions")} placeholder="product, engineering" className="bg-[#F4F4F5] border-black/10 rounded-none" /></div>
                </div>
                <div className="space-y-2">
                  <Label>Resume <span className="text-muted-foreground font-normal">(optional — PDF/DOC)</span></Label>
                  <input type="file" accept=".pdf,.doc,.docx,.txt" data-testid="cand-signup-resume"
                    onChange={(e) => setF({ ...f, resume: e.target.files?.[0] })}
                    className="w-full text-sm file:mr-3 file:py-2 file:px-4 file:border file:border-black/10 file:bg-[#F4F4F5] file:text-sm file:cursor-pointer" />
                  {f.resume && <p className="text-xs text-muted-foreground">Selected: {f.resume.name}</p>}
                </div>
                <Button type="submit" disabled={saving} data-testid="cand-signup-submit" className="w-full h-11 rounded-none bg-primary hover:bg-[#09090B] transition-colors">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create candidate account"}
                </Button>
                <p className="text-xs text-muted-foreground text-center">Your profile is private. Only recruiters can view it.</p>
              </form>
            </div>
          </div>
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}
